// Core
import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = ['tableBody', 'noAttributesMessage'];

  tableBodyTarget: HTMLTableSectionElement;
  noAttributesMessageTarget: HTMLElement;

  private currentIndex: number = 0;

  connect() {
    // Count existing rows to set the starting index for new attributes
    const existingRows = this.tableBodyTarget.querySelectorAll('tr');
    this.currentIndex = existingRows.length;

    // Initialize values field visibility for existing rows
    this.initializeExistingRows();

    // Setup real-time input listeners to clear errors on user interaction
    this.setupInputListeners();

    // Setup form submission validation
    this.setupFormValidation();
  }

  /**
   * Add a new attribute row to the table
   */
  addAttribute(event: Event) {
    event.preventDefault();

    // Hide "no attributes" message
    this.hideNoAttributesMessage();

    const index = this.currentIndex;
    const prefix = `item_digital_section[item_digital_attributes_attributes][${index}]`;

    const row = document.createElement('tr');
    row.classList.add('new-attribute-row');
    row.innerHTML = `
      <td>
        <input type="text"
               name="${prefix}[name]"
               class="form-control attribute-name-input"
               placeholder="Attribute name" />
      </td>
      <td>
        <select name="${prefix}[field_type]"
                class="form-control field-type-select"
                data-action="change->items--edit-digital-section#fieldTypeChanged">
          <option value="">Select Type</option>
          <option value="text">Text</option>
          <option value="date">Date</option>
          <option value="boolean">Boolean</option>
          <option value="select">Select</option>
          <option value="multiselect">Multiselect</option>
          <option value="file">File</option>
        </select>
      </td>
      <td>
        <select name="${prefix}[scope]" class="form-control attribute-scope-select">
          <option value="">Select Scope</option>
          <option value="style_level">Style level</option>
          <option value="item_level">Item level</option>
        </select>
      </td>
      <td>
        <textarea name="${prefix}[values]"
                  rows="2"
                  class="form-control values-field"
                  placeholder="Option 1, Option 2, Option 3 (for Select/Multiselect only)"
                  style="display:none;"></textarea>
      </td>
      <td>
        <select name="${prefix}[active]" class="form-control">
          <option value="true" selected>Active</option>
          <option value="false">Inactive</option>
        </select>
      </td>
      <td style="text-align: center;">
        <button type="button"
                data-action="click->items--edit-digital-section#removeNewAttribute"
                title="Remove Attribute"
                style="background: none; border: none; color: #e74c3c; font-size: 18px; cursor: pointer;">
          &#10005;
        </button>
      </td>
    `;

    this.tableBodyTarget.appendChild(row);
    this.currentIndex++;

    // Focus on the name field of the new row
    const nameInput = row.querySelector('input[name*="[name]"]') as HTMLInputElement;
    if (nameInput) nameInput.focus();

    // Add event listeners to dynamically add/remove required attributes based on user input
    this.setupDynamicRequiredAttributes(row);
  }

  /**
   * Setup event listeners to add/remove required attributes based on user input
   * This allows empty rows to not block form submission
   */
  private setupDynamicRequiredAttributes(row: Element) {
    const nameInput = row.querySelector('.attribute-name-input') as HTMLInputElement;
    const fieldTypeSelect = row.querySelector('.field-type-select') as HTMLSelectElement;
    const scopeSelect = row.querySelector('.attribute-scope-select') as HTMLSelectElement;

    // Function to check if row should have required attributes
    const updateRequiredAttributes = () => {
      const hasName = nameInput?.value?.trim() || false;
      const hasFieldType = fieldTypeSelect?.value?.trim() || false;
      const hasScope = scopeSelect?.value?.trim() || false;
      
      // If any field is filled, make all required fields required
      const shouldBeRequired = hasName || hasFieldType || hasScope;

      if (nameInput) {
        if (shouldBeRequired) {
          nameInput.setAttribute('required', 'required');
        } else {
          nameInput.removeAttribute('required');
        }
      }

      if (fieldTypeSelect) {
        if (shouldBeRequired) {
          fieldTypeSelect.setAttribute('required', 'required');
        } else {
          fieldTypeSelect.removeAttribute('required');
        }
      }

      if (scopeSelect) {
        if (shouldBeRequired) {
          scopeSelect.setAttribute('required', 'required');
        } else {
          scopeSelect.removeAttribute('required');
        }
      }
    };

    // Add listeners to all fields
    if (nameInput) {
      nameInput.addEventListener('input', updateRequiredAttributes);
      nameInput.addEventListener('blur', updateRequiredAttributes);
    }
    if (fieldTypeSelect) {
      fieldTypeSelect.addEventListener('change', updateRequiredAttributes);
    }
    if (scopeSelect) {
      scopeSelect.addEventListener('change', updateRequiredAttributes);
    }
  }

  /**
   * Remove a dynamically added attribute row
   */
  removeNewAttribute(event: Event) {
    event.preventDefault();
    const button = event.currentTarget as HTMLElement;
    const row = button.closest('tr');
    if (row && confirm('Are you sure you want to remove this attribute?')) {
      row.remove();
      this.checkEmptyState();
    }
  }

  /**
   * Toggle values/options field visibility based on field type selection
   */
  fieldTypeChanged(event: Event) {
    const select = event.currentTarget as HTMLSelectElement;
    const row = select.closest('tr');
    if (!row) return;

    const valuesField = row.querySelector('.values-field, textarea[name*="[values]"]') as HTMLTextAreaElement;
    if (!valuesField) return;

    this.updateValuesVisibility(select, valuesField, row.classList.contains('new-attribute-row'));

    // Clear error styling on the field type select
    this.clearFieldError(select);

    // Clear values field error when switching away from select/multiselect
    if (!['select', 'multiselect'].includes(select.value)) {
      this.clearFieldError(valuesField);
    }
  }

  // ─── Private helpers ───────────────────────────────────────────────

  /**
   * Setup form submission validation
   */
  private setupFormValidation() {
    const form = this.element.querySelector('form');
    if (!form) return;

    form.addEventListener('submit', (e: Event) => {
      // First, clean up empty rows by removing required attributes
      this.cleanupEmptyRows();
      
      // Clear all error states before validation
      this.clearAllErrors();
      
      // Then validate filled rows
      if (!this.validateForm()) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Clear all required attributes before allowing submission
      // This ensures HTML5 validation doesn't interfere
      this.clearAllRequiredAttributes();
      
      // Allow form to submit
      return true;
    });
  }

  /**
   * Remove required attributes from completely empty rows to allow form submission
   */
  private cleanupEmptyRows() {
    const allRows = this.tableBodyTarget.querySelectorAll('tr');
    allRows.forEach((row: Element) => {
      const destroyCheckbox = row.querySelector('input[name*="[_destroy]"][type="checkbox"]') as HTMLInputElement;
      if (destroyCheckbox && destroyCheckbox.checked) return;

      const nameField = row.querySelector('input[name*="[name]"]') as HTMLInputElement;
      const fieldTypeSelect = row.querySelector('select[name*="[field_type]"]') as HTMLSelectElement;
      const scopeSelect = row.querySelector('select[name*="[scope]"]') as HTMLSelectElement;

      const nameValue = nameField?.value?.trim() || '';
      const fieldTypeValue = fieldTypeSelect?.value?.trim() || '';
      const scopeValue = scopeSelect?.value?.trim() || '';

      // If all fields are empty, remove required attributes
      if (!nameValue && !fieldTypeValue && !scopeValue) {
        if (nameField) nameField.removeAttribute('required');
        if (fieldTypeSelect) fieldTypeSelect.removeAttribute('required');
        if (scopeSelect) scopeSelect.removeAttribute('required');
      }
    });
  }

  /**
   * Validate all attribute rows (existing + new) before form submission
   */
  private validateForm(): boolean {
    const errors: string[] = [];
    let hasErrors = false;

    const allRows = this.tableBodyTarget.querySelectorAll('tr');
    allRows.forEach((row: Element, index: number) => {
      // Skip rows marked for destruction (existing attributes with "Remove" checked)
      const destroyCheckbox = row.querySelector('input[name*="[_destroy]"][type="checkbox"]') as HTMLInputElement;
      if (destroyCheckbox && destroyCheckbox.checked) return;

      const nameField = row.querySelector('input[name*="[name]"]') as HTMLInputElement;
      const fieldTypeSelect = row.querySelector('select[name*="[field_type]"]') as HTMLSelectElement;
      const scopeSelect = row.querySelector('select[name*="[scope]"]') as HTMLSelectElement;
      const valuesField = row.querySelector('textarea[name*="[values]"]') as HTMLTextAreaElement;

      // Skip completely empty rows (all fields empty) - allow user to leave empty rows
      const nameValue = nameField?.value?.trim() || '';
      const fieldTypeValue = fieldTypeSelect?.value?.trim() || '';
      const scopeValue = scopeSelect?.value?.trim() || '';
      
      // If all fields are empty, skip validation for this row
      if (!nameValue && !fieldTypeValue && !scopeValue) {
        return;
      }

      // If any field is filled, all required fields must be filled
      const attributeName = nameValue || `Attribute ${index + 1}`;

      // Validate name
      if (!nameValue) {
        hasErrors = true;
        errors.push(`"${attributeName}" requires a name`);
        this.setFieldError(nameField, 'Name is required');
      } else {
        this.clearFieldError(nameField);
      }

      // Validate field type
      if (!fieldTypeValue) {
        hasErrors = true;
        errors.push(`"${attributeName}" requires a Field Type to be selected`);
        this.setFieldError(fieldTypeSelect, 'Field Type is required');
      } else {
        this.clearFieldError(fieldTypeSelect);
      }

      // Validate scope
      if (!scopeValue) {
        hasErrors = true;
        errors.push(`"${attributeName}" requires a Scope to be selected`);
        this.setFieldError(scopeSelect, 'Scope is required');
      } else {
        this.clearFieldError(scopeSelect);
      }

      // Validate options for select/multiselect fields
      if (fieldTypeSelect && ['select', 'multiselect'].includes(fieldTypeValue)) {
        if (!valuesField?.value?.trim()) {
          hasErrors = true;
          errors.push(`"${attributeName}" (${fieldTypeValue}) requires options to be filled`);
          this.setFieldError(valuesField, 'Options are required for Select/Multiselect fields');
        } else {
          this.clearFieldError(valuesField);
        }
      } else if (valuesField) {
        this.clearFieldError(valuesField);
      }
    });

    if (hasErrors) {
      // alert('Please fix the following errors:\n\n' + errors.join('\n'));
      return false;
    }

    return true;
  }

  /**
   * Initialize values field visibility for existing attribute rows on page load
   */
  private initializeExistingRows() {
    const rows = this.tableBodyTarget.querySelectorAll('tr');
    rows.forEach((row: Element) => {
      const fieldTypeSelect = row.querySelector('select[name*="[field_type]"]') as HTMLSelectElement;
      const valuesField = row.querySelector('.values-field, textarea[name*="[values]"]') as HTMLTextAreaElement;

      if (fieldTypeSelect && valuesField) {
        this.updateValuesVisibility(fieldTypeSelect, valuesField, false);
      }
    });
  }

  /**
   * Show or hide "no attributes" message based on empty tbody
   */
  private checkEmptyState() {
    const rows = this.tableBodyTarget.querySelectorAll('tr');
    if (rows.length === 0) {
      this.showNoAttributesMessage();
    }
  }

  /**
   * Setup input/change event listeners to clear error styling in real time
   */
  private setupInputListeners() {
    // Clear errors when user types in input or textarea
    this.tableBodyTarget.addEventListener('input', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        const input = target as HTMLInputElement | HTMLTextAreaElement;
        if (input.value.trim() !== '') {
          this.clearFieldError(target);
        }
      }
    });

    // Clear errors when user changes a select field
    this.tableBodyTarget.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target && target.tagName === 'SELECT') {
        const select = target as HTMLSelectElement;
        if (select.value !== '') {
          this.clearFieldError(target);
        }
      }
    });
  }

  /**
   * Show or hide values textarea based on field type
   */
  private updateValuesVisibility(selectEl: HTMLSelectElement, valuesField: HTMLTextAreaElement, isNewRow: boolean) {
    if (['select', 'multiselect'].includes(selectEl.value)) {
      valuesField.style.display = 'block';
    } else {
      valuesField.style.display = 'none';
      // Only clear value for new rows (preserve existing data on existing rows)
      if (isNewRow) {
        valuesField.value = '';
      }
    }
  }

  /**
   * Highlight a field with error border and message
   */
  private setFieldError(element: HTMLElement | null, message: string) {
    if (!element) return;
    element.style.borderColor = '#e74c3c';
    element.style.borderWidth = '2px';

    const parent = element.parentElement;
    if (parent) {
      let errorMsg = parent.querySelector('.field-error-message') as HTMLElement;
      if (!errorMsg) {
        errorMsg = document.createElement('span');
        errorMsg.className = 'field-error-message';
        errorMsg.style.cssText = 'color: #e74c3c; font-size: 0.8rem; margin-top: 0.25rem; display: block;';
        parent.appendChild(errorMsg);
      }
      errorMsg.textContent = message;
    }
  }

  /**
   * Clear error styling from a field
   */
  private clearFieldError(element: HTMLElement | null) {
    if (!element) return;
    element.style.borderColor = '';
    element.style.borderWidth = '';

    const parent = element.parentElement;
    if (parent) {
      const errorMsg = parent.querySelector('.field-error-message');
      if (errorMsg) errorMsg.remove();
    }
  }

  /**
   * Hide the "no attributes" message safely
   */
  private hideNoAttributesMessage() {
    try {
      if (this.noAttributesMessageTarget) {
        this.noAttributesMessageTarget.style.display = 'none';
      }
    } catch (e) {
      // Target may not exist if there are existing attributes
    }
  }

  /**
   * Show the "no attributes" message safely
   */
  private showNoAttributesMessage() {
    try {
      if (this.noAttributesMessageTarget) {
        this.noAttributesMessageTarget.style.display = 'block';
      }
    } catch (e) {
      // Target may not exist
    }
  }

  /**
   * Clear all required attributes from all fields
   * This prevents HTML5 validation from blocking submission
   */
  private clearAllRequiredAttributes() {
    const allRows = this.tableBodyTarget.querySelectorAll('tr');
    allRows.forEach((row: Element) => {
      const inputs = row.querySelectorAll('input, select, textarea');
      inputs.forEach((input: Element) => {
        (input as HTMLElement).removeAttribute('required');
      });
    });
  }

  /**
   * Clear all error styling from all fields
   */
  private clearAllErrors() {
    const allRows = this.tableBodyTarget.querySelectorAll('tr');
    allRows.forEach((row: Element) => {
      const inputs = row.querySelectorAll('input, select, textarea');
      inputs.forEach((input: Element) => {
        this.clearFieldError(input as HTMLElement);
      });
    });
  }
}
